import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faEnvelope, faPhone, faSignOutAlt, faAddressBook, 
    faCloudUploadAlt, faEye, faPenToSquare, faNewspaper, 
    faProjectDiagram, faFolderPlus, faArrowLeft, faHome,
    faChartLine, faCalendar, faClock, faBars, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useDashboardData } from '../hooks/useDashboardData';
import PropTypes from 'prop-types';

// Import all components as pages
import CreateBlogPost from '../components/CreateBlogPost.jsx';
import BlogManagement from '../components/BlogManagement.jsx';
import CreateProject from '../components/CreateProject.jsx';
import ProjectManagement from '../components/ProjectManagement.jsx';
import ViewContacts from '../components/ViewContacts.jsx';
import UploadMedia from '../components/UploadMedia.jsx';
import MediaManagement from '../components/MediaManagement.jsx';

// Dashboard Home Component
const DashboardHome = ({ darkMode, user, stats, activities, loading, error, onRefresh }) => {
    return (
        <div className="space-y-8">
            {/* Welcome Header */}
            <div className={`p-4  rounded-2xl ${darkMode ? 'bg-gradient-to-r from-gray-800 to-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                        <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${darkMode ? 'bg-gradient-to-tr from-blue-500 to-purple-600' : 'bg-gradient-to-tr from-blue-400 to-purple-500'} flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg`}>
                            {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-center sm:text-left">
                            <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                                Welcome back, {user.name}! 👋
                            </h1>
                            <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                Here&apos;s what&apos;s happening with your portfolio today.
                            </p>
                        </div>
                    </div>
                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            disabled={loading}
                            className={`px-4 py-2 rounded-lg ${
                                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                            } transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                        >
                            <FontAwesomeIcon icon={faChartLine} className={loading ? 'animate-spin' : ''} />
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    )}
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'} border`}>
                    <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {error}
                    </p>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {/* Projects Stats */}
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg hover:shadow-xl transition-shadow`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Projects</p>
                            {loading ? (
                                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mt-2"></div>
                            ) : (
                                <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {stats?.projects?.total || 0}
                                </p>
                            )}
                            <p className="text-sm text-green-500 mt-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faChartLine} className="text-xs" />
                                {loading ? '...' : `+${stats?.projects?.growth || 0} this month`}
                            </p>
                        </div>
                        <div className={`w-14 h-14 rounded-xl ${darkMode ? 'bg-blue-600/20' : 'bg-blue-100'} flex items-center justify-center`}>
                            <FontAwesomeIcon icon={faProjectDiagram} className={`text-2xl ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                    </div>
                </div>

                {/* Blog Posts Stats */}
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg hover:shadow-xl transition-shadow`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Blog Posts</p>
                            {loading ? (
                                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mt-2"></div>
                            ) : (
                                <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {stats?.blogs?.total || 0}
                                </p>
                            )}
                            <p className="text-sm text-green-500 mt-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faEye} className="text-xs" />
                                {loading ? '...' : `${stats?.blogs?.totalViews || 0} views`}
                            </p>
                        </div>
                        <div className={`w-14 h-14 rounded-xl ${darkMode ? 'bg-green-600/20' : 'bg-green-100'} flex items-center justify-center`}>
                            <FontAwesomeIcon icon={faNewspaper} className={`text-2xl ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                    </div>
                </div>

                {/* Media Files Stats */}
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg hover:shadow-xl transition-shadow`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Media Files</p>
                            {loading ? (
                                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mt-2"></div>
                            ) : (
                                <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {stats?.media?.total || 0}
                                </p>
                            )}
                            <p className="text-sm text-purple-500 mt-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faCloudUploadAlt} className="text-xs" />
                                {loading ? '...' : `+${stats?.media?.growth || 0} uploaded`}
                            </p>
                        </div>
                        <div className={`w-14 h-14 rounded-xl ${darkMode ? 'bg-purple-600/20' : 'bg-purple-100'} flex items-center justify-center`}>
                            <FontAwesomeIcon icon={faCloudUploadAlt} className={`text-2xl ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                    </div>
                </div>

                {/* Messages Stats */}
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg hover:shadow-xl transition-shadow`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Messages</p>
                            {loading ? (
                                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mt-2"></div>
                            ) : (
                                <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {stats?.contacts?.total || 0}
                                </p>
                            )}
                            <p className="text-sm text-orange-500 mt-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                                {loading ? '...' : `${stats?.contacts?.unread || 0} unread`}
                            </p>
                        </div>
                        <div className={`w-14 h-14 rounded-xl ${darkMode ? 'bg-orange-600/20' : 'bg-orange-100'} flex items-center justify-center`}>
                            <FontAwesomeIcon icon={faAddressBook} className={`text-2xl ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity & Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <div className={`p-4 sm:p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg`}>
                    <h3 className={`text-lg sm:text-xl font-bold mb-4 sm:mb-6 ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                        <FontAwesomeIcon icon={faClock} className="text-blue-500" />
                        Recent Activity
                    </h3>
                    <div className="space-y-4">
                        {loading ? (
                            // Loading skeleton
                            Array.from({ length: 3 }).map((_, index) => (
                                <div key={index} className="flex items-center gap-4 p-3 rounded-lg">
                                    <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
                                    <div className="flex-1">
                                        <div className="w-3/4 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mb-2"></div>
                                        <div className="w-1/2 h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                                    </div>
                                </div>
                            ))
                        ) : activities && activities.length > 0 ? (
                            activities.map((activity, index) => {
                                const getActivityColorClasses = (type) => {
                                    switch (type) {
                                        case 'blog': 
                                            return {
                                                bgLight: 'bg-blue-100',
                                                bgDark: 'bg-blue-600/20',
                                                textLight: 'text-blue-600',
                                                textDark: 'text-blue-400'
                                            };
                                        case 'project': 
                                            return {
                                                bgLight: 'bg-green-100',
                                                bgDark: 'bg-green-600/20',
                                                textLight: 'text-green-600',
                                                textDark: 'text-green-400'
                                            };
                                        case 'media': 
                                            return {
                                                bgLight: 'bg-purple-100',
                                                bgDark: 'bg-purple-600/20',
                                                textLight: 'text-purple-600',
                                                textDark: 'text-purple-400'
                                            };
                                        case 'contact': 
                                            return {
                                                bgLight: 'bg-orange-100',
                                                bgDark: 'bg-orange-600/20',
                                                textLight: 'text-orange-600',
                                                textDark: 'text-orange-400'
                                            };
                                        default: 
                                            return {
                                                bgLight: 'bg-gray-100',
                                                bgDark: 'bg-gray-600/20',
                                                textLight: 'text-gray-600',
                                                textDark: 'text-gray-400'
                                            };
                                    }
                                };

                                const formatTimeAgo = (timestamp) => {
                                    const now = new Date();
                                    const activityTime = new Date(timestamp);
                                    const diffInHours = Math.floor((now - activityTime) / (1000 * 60 * 60));
                                    
                                    if (diffInHours < 1) return 'Just now';
                                    if (diffInHours < 24) return `${diffInHours}h ago`;
                                    const diffInDays = Math.floor(diffInHours / 24);
                                    if (diffInDays < 7) return `${diffInDays}d ago`;
                                    return activityTime.toLocaleDateString();
                                };

                                const colorClasses = getActivityColorClasses(activity.type);
                                
                                // Get the appropriate image URL based on activity type
                                const getActivityImage = (activity) => {
                                    if (activity.type === 'media') {
                                        // For media, use the URL directly if it's an image, otherwise use type-based icon
                                        if (activity.metadata.mediaType === 'image') {
                                            return activity.imageUrl || '/images/default-activity.png';
                                        } else if (activity.metadata.mediaType === 'video') {
                                            return '/images/video-icon.png';
                                        } else {
                                            return '/images/file-icon.png';
                                        }
                                    } else if (activity.type === 'contact') {
                                        // For contacts, use the message icon
                                        return '/images/message-icon.png';
                                    }
                                    // For blog and project, use the provided image or default
                                    return activity.imageUrl || '/images/default-activity.png';
                                };

                                // Get the appropriate icon based on activity type
                                const getActivityBadgeIcon = (type) => {
                                    switch (type) {
                                        case 'blog': return faPenToSquare;
                                        case 'project': return faFolderPlus;
                                        case 'media': return faCloudUploadAlt;
                                        case 'contact': return faEnvelope;
                                        default: return faClock;
                                    }
                                };

                                return (
                                    <div key={index} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                        <div className="relative">
                                            {activity.type === 'contact' ? (
                                                // For messages, show an icon in a colored circle
                                                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                                    <FontAwesomeIcon 
                                                        icon={faEnvelope} 
                                                        className="text-blue-500 dark:text-blue-400 text-xl"
                                                    />
                                                </div>
                                            ) : (
                                                // For other activities, show the image
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                                                    <img 
                                                        src={getActivityImage(activity)}
                                                        alt={activity.title}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = activity.type === 'media' 
                                                                ? '/images/file-icon.png' 
                                                                : '/images/default-activity.png';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full ${darkMode ? colorClasses.bgDark : colorClasses.bgLight} flex items-center justify-center`}>
                                                <FontAwesomeIcon 
                                                    icon={getActivityBadgeIcon(activity.type)} 
                                                    className={`text-xs ${darkMode ? colorClasses.textDark : colorClasses.textLight}`} 
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                                    {activity.section}
                                                </span>
                                                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {formatTimeAgo(activity.timestamp)}
                                                </span>
                                            </div>
                                            <p className={`font-medium mt-1 ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>
                                                {activity.title}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                {activity.metadata.views !== undefined && (
                                                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        👁️ {activity.metadata.views}
                                                    </span>
                                                )}
                                                {activity.metadata.likes !== undefined && (
                                                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        ❤️ {activity.metadata.likes}
                                                    </span>
                                                )}
                                                {activity.metadata.status && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                                        activity.metadata.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                                        activity.metadata.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                        {activity.metadata.status}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8">
                                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    No recent activity
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className={`p-4 sm:p-6 h-fit rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border shadow-lg`}>
                    <h3 className={`text-lg sm:text-xl font-bold mb-4 sm:mb-6 ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                        <FontAwesomeIcon icon={faCalendar} className="text-green-500" />
                        Account Information
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${darkMode ? 'bg-blue-600/20' : 'bg-blue-100'} flex items-center justify-center`}>
                                <FontAwesomeIcon icon={faUser} className={`text-lg ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                            </div>
                            <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Full Name</p>
                                <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${darkMode ? 'bg-green-600/20' : 'bg-green-100'} flex items-center justify-center`}>
                                <FontAwesomeIcon icon={faEnvelope} className={`text-lg ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                            </div>
                            <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Email Address</p>
                                <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>{user.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${darkMode ? 'bg-purple-600/20' : 'bg-purple-100'} flex items-center justify-center`}>
                                <FontAwesomeIcon icon={faPhone} className={`text-lg ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                            </div>
                            <div>
                                <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone Number</p>
                                <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.mobileNo}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const NewDashboard = ({ onLogout, user: propUser }) => {
  const { darkMode } = useTheme();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('home');
    const [editingPost, setEditingPost] = useState(null);
    const [editingProject, setEditingProject] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    // Dashboard data hook
    const { stats, activities, profile, loading, error, refreshData } = useDashboardData();
    
    // Use the user from props or fallback to profile from useDashboardData
    const user = propUser || profile;

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
            if (window.innerWidth >= 1024) {
                setSidebarOpen(false); // Close sidebar on desktop
            }
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    // Show loading state only if we don't have user data yet
    if (!user) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-lg">Loading Dashboard...</p>
                    <button 
                        onClick={() => window.location.href = '/login'} 
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    const menuItems = [
        { id: 'home', label: 'Dashboard Home', icon: faHome, color: 'blue' },
        { id: 'contacts', label: 'View Contacts', icon: faAddressBook, color: 'blue' },
        { id: 'upload-media', label: 'Upload Media', icon: faCloudUploadAlt, color: 'green' },
        { id: 'manage-media', label: 'Manage Media', icon: faEye, color: 'purple' },
        { id: 'create-blog', label: 'Create Blog', icon: faPenToSquare, color: 'orange' },
        { id: 'manage-blogs', label: 'Manage Blogs', icon: faNewspaper, color: 'indigo' },
        { id: 'create-project', label: 'Add Project', icon: faFolderPlus, color: 'cyan' },
        { id: 'manage-projects', label: 'Manage Projects', icon: faProjectDiagram, color: 'pink' },
    ];

    const renderMainContent = () => {
        switch (activeSection) {
            case 'home':
                return <DashboardHome darkMode={darkMode} user={user} stats={stats} activities={activities} loading={loading} error={error} onRefresh={refreshData} />;
            case 'contacts':
                return <ViewContacts darkMode={darkMode} onClose={() => {}} onDelete={() => {}} />;
            case 'upload-media':
                return <UploadMedia darkMode={darkMode} onClose={() => {}} onMediaUploaded={() => {}} />;
            case 'manage-media':
                return <MediaManagement darkMode={darkMode} onClose={() => {}} onEditMedia={() => {}} />;
            case 'create-blog':
                return (
                    <CreateBlogPost 
                        darkMode={darkMode}
                        onClose={() => setActiveSection('home')}
                        onPostCreated={() => setActiveSection('manage-blogs')}
                        editPost={editingPost}
                    />
                );
            case 'manage-blogs':
                return (
                    <BlogManagement 
                        darkMode={darkMode}
                        onClose={() => setActiveSection('home')}
                        onEditPost={(post) => {
                            setEditingPost(post);
                            setActiveSection('create-blog');
                        }}
                    />
                );
            case 'create-project':
                return (
                    <CreateProject 
                        darkMode={darkMode}
                        onClose={() => setActiveSection('home')}
                        onProjectCreated={() => setActiveSection('manage-projects')}
                        editProject={editingProject}
                    />
                );
            case 'manage-projects':
                return (
                    <ProjectManagement 
                        darkMode={darkMode}
                        onClose={() => setActiveSection('home')}
                        onEditProject={(project) => {
                            setEditingProject(project);
                            setActiveSection('create-project');
                        }}
                    />
                );
            default:
                return (
                    <DashboardHome 
                        darkMode={darkMode} 
                        user={user || profile} 
                        stats={stats}
                        activities={activities}
                        loading={loading}
                        error={error}
                        onRefresh={refreshData}
                    />
                );
        }
    };

    return (
        <div className={`flex h-screen ${darkMode ? 'bg-gray-950' : 'bg-gray-50'} relative`}>
            {/* Mobile Menu Button */}
            {isMobile && ( 
                <div className='fixed top-4 left-4 z-30 flex gap-2'>
                <button
                    onClick={toggleSidebar}
                    className={` p-3 rounded-lg ${
                        darkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-100 text-gray-900'
                    } shadow-lg transition-colors lg:hidden`}
                >
                    <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className="text-lg" />
                </button>
                <button
                onClick={() => navigate('/')}
                className={` z-30 p-3 rounded-lg ${
                    darkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-100 text-gray-900'
                } shadow-lg transition-colors lg:hidden`}
                >
                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                </button>
</div>

            )}

            {/* Mobile Overlay */}
            {isMobile && sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar */}
            <div className={`lg:w-80 w-64 h-screen  fixed left-0 z-50 top-0 ${
                darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            } border-r shadow-xl z-40 transform transition-transform duration-300 ease-in-out  ${
                isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
            } lg:translate-x-0 flex flex-col`}>
                {/* Profile Section */}
                <div className={`px-4 relative py-2 border-b ${darkMode ? 'border-gray-700 ' : 'border-gray-200 text-black'}`}>
                    <button className={`absolute top-4 -right-7 z-50 ${sidebarOpen && isMobile  ? 'block' : 'hidden'}`} onClick={closeSidebar}>
                        <FontAwesomeIcon icon={faTimes} className={`text-lg px-2 py-1  rounded-r-lg ${darkMode ? 'text-white bg-gray-800' : 'text-gray-900 bg-white'}`} />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-gradient-to-tr from-blue-500 to-purple-600' : 'bg-gradient-to-tr from-blue-400 to-purple-500'} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                            {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h2 className={`lg:text-xl text-md font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</h2>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Administrator</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 p-4 space-y-2">
                    <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 px-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Management
                    </h3>
                    
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveSection(item.id);
                                if (isMobile) closeSidebar();
                            }}
                            className={`w-full flex  items-center gap-3 p-2 rounded-xl transition-all duration-200 ${
                                activeSection === item.id
                                    ? `${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'} shadow-lg`
                                    : `${darkMode ? 'hover:bg-gray-800 text-gray-300 hover:text-white' : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'}`
                            } group`}
                        >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                activeSection === item.id
                                    ? `${darkMode ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-700'}`
                                    : `${darkMode ? `bg-${item.color}-600/20 text-${item.color}-400 group-hover:bg-${item.color}-600/30` : `bg-${item.color}-100 text-${item.color}-600 group-hover:bg-${item.color}-200`}`
                            }`}>
                                <FontAwesomeIcon icon={item.icon} className="text-sm" />
                            </div>
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </div>

                {/* Bottom Actions */}
                <div className={`lg:p-4 p-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-inherit flex-shrink-0`}>
                    <button 
                        onClick={() => {
                            navigate('/');
                            if (isMobile) closeSidebar();
                        }}
                        className={`w-full hidden lg:block mb-3 px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} transition-colors flex items-center justify-center gap-2 font-medium`}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="text-sm rotate-180 mr-2" />
                        Back to Home
                    </button>
                    <button 
                        onClick={() => {
                            onLogout();
                            if (isMobile) closeSidebar();
                        }}
                        className={`w-full  px-3 py-2 rounded-lg ${darkMode ? 'bg-red-600/20 hover:bg-red-600/30 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-600'} transition-colors flex items-center justify-center gap-2 font-medium`}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="text-sm" />
                        Logout
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className={`flex-1 min-h-screen overflow-y-auto custom-scrollbar transition-all duration-300 ease-in-out ${
                isMobile ? 'ml-0' : 'ml-80'
            } lg:ml-80`}>
                <div className={`p-4 sm:p-6 lg:p-8 ${
                    isMobile ? 'pt-20' : 'pt-8'
                } lg:pt-8`}>
                    <div className="max-w-7xl mx-auto">
                        {renderMainContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

NewDashboard.propTypes = {
    onLogout: PropTypes.func.isRequired,
    user: PropTypes.object
};

DashboardHome.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    user: PropTypes.object.isRequired,
    stats: PropTypes.object,
    activities: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRefresh: PropTypes.func
};

export default NewDashboard;
